import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../shared/components/Layout';
import { TrialOrderClItems, TrialOrderClItem, TrialLensOrderParams } from '../types';
import { convertKeysToSnakeCase } from '@/utils/format';
import createTrialLens from '../api/createTrialLens';
import { useOrderContext } from '@/providers/OrderProvider';
import InfoDisplay from '@/components/InfoDisplay';

const TrialLensConfirm: React.FC = () => {
  const navigate = useNavigate();
  const { arrivalTimeZoneLabels } = useOrderContext();

  const [facilityName, setFacilityName] = useState<string>('');
  const [facilityCode, setFacilityCode] = useState<string>('');
  const [facilityId, setFacilityId] = useState<number | undefined>();
  const [expectedShipDate, setExpectedShipDate] = useState<string>('');
  const [shortestArrivalDate, setShortestArrivalDate] = useState<string>('');

  const [note, setNote] = useState<string>();
  const [arrivalDate, setArrivalDate] = useState<string>('');
  const [arrivalTimeZone, setArrivalTimeZone] = useState<string>('no_preference');
  const [lenses, setLenses] = useState<TrialOrderClItems>({});
  const [productName, setProductName] = useState<string>();

  useEffect(() => {
    const storedOrderParams = localStorage.getItem('orderParams');

    if (storedOrderParams) {
      const orderParams = JSON.parse(storedOrderParams);

      const {
        note,
        expectedShipDate,
        shortestArrivalDate,
        arrivalDate,
        arrivalTimeZone,
        orderClItemsAttributes,
      } = orderParams.order;

      setNote(note);
      setExpectedShipDate(expectedShipDate);
      setShortestArrivalDate(shortestArrivalDate);
      setArrivalDate(arrivalDate);
      setArrivalTimeZone(arrivalTimeZone);
      setLenses(orderClItemsAttributes);
    }

    const storedFacilityName = localStorage.getItem("facilityName");
    if(storedFacilityName) {
      setFacilityName(JSON.parse(storedFacilityName));
    }

    const storedFacilityCode = localStorage.getItem("facilityCode");
    if(storedFacilityCode) {
      setFacilityCode(JSON.parse(storedFacilityCode));
    }

    const storedFacilityId = localStorage.getItem("facilityId");
    if(storedFacilityId) {
      setFacilityId(JSON.parse(storedFacilityId));
    }

    const storedProductName = localStorage.getItem("productName");
    if(storedProductName) {
      setProductName(JSON.parse(storedProductName));
    }
  }, []);

  const convertedArrivalTimeZoneLabels = convertKeysToSnakeCase(arrivalTimeZoneLabels);

  const handleOnClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    const params: TrialLensOrderParams = {
      order: {
        order_type: 'trial',
        facilityId: facilityId,
        note: note,
        arrivalDate: arrivalDate,
        arrivalTimeZone: arrivalTimeZone,
        expectedShipDate: expectedShipDate,
        shortestArrivalDate: shortestArrivalDate,
        orderClItemsAttributes: lenses
      },
    }

    try {
      const res = await createTrialLens(params);
      if(res.status === 201) {
        localStorage.removeItem("orderParams");
        localStorage.removeItem("facilityName");
        localStorage.removeItem("facilityCode");
        localStorage.removeItem("facilityId");
        localStorage.removeItem("productName");

        navigate("/facility/orders/trial_lens/complete");
      } else if(res.status !== 201) {
        navigate("/facility/orders/trial_lens");
        window.scrollTo(0, 0);
        console.error(`status: ${res.status}, message: ${res.statusText}`);
      }

    } catch (err: any) {
      navigate("/facility/orders/trial_lens");
      window.scrollTo(0, 0);
      console.log('An unexpected error occurred:', err.message);
    }
  }

  const renderPreviousPage = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    navigate("/facility/orders/trial_lens", { state: { fromConfirmPage: true } });
    window.scrollTo(0, 0);
  }

  return (
    <React.Fragment>
      <Layout>
        <main id="page-facility_login" className="wrapper">
          <section className="sec-contact">
            <div className="inner">
              <h1 className="c-ttl__01">トライアルレンズ注文確認</h1>
              <div className="contact-box">
                <div className="contact-box__left">
                  <div className="mbpc-98 mbsp-32">
                    <form action="/">
                      <div className="contact-box__form js-item" id="id-01">
                        <h2 className="c-ttl__02">お客様情報</h2>
                        <InfoDisplay label="コードNo" value={facilityCode} />
                        <InfoDisplay label="得意先名" value={facilityName} />
                      </div>
                      <div className="contact-box__form mbsp-29 js-item" id="id-02">
                        <h2 className="c-ttl__02">注文内容</h2>
                        <InfoDisplay label="対象レンズ" value={productName} />
                        <div className="contact-box__item contact-box__item--start">
                          <label className="contact-box__ttl">
                            <span>レンズデータ</span>
                          </label>
                          <div className="contact-box__ct">
                            {
                              lenses && Object.entries(lenses).map(([id, item]) => {
                                const lens = item as TrialOrderClItem;
                                return (
                                  <p key={id} className="contact-box__desc mbpc-15 mbsp-15">
                                    FC: {lens.fc}&ensp;
                                    TP: {lens.tp}&ensp;
                                    {lens.cyl !== '' && <>CYL: {lens.cyl}&ensp;</>}
                                    DIA: {lens.dia}&ensp;
                                    数量: {lens.qty}
                                  </p>
                                )
                              })
                            }
                          </div>
                        </div>
                        <InfoDisplay label="備考" value={note} />
                        <InfoDisplay label="着日指定" value={arrivalDate ? arrivalDate : "最短お届け日"} />
                        <InfoDisplay label="ご希望時間帯" value={convertedArrivalTimeZoneLabels[arrivalTimeZone]} />
                      </div>
                      <button className="c-btn__01" onClick={handleOnClick}>注文確定</button>
                      <div className="c-al--c">
                        <button className="c-btn__02" onClick={renderPreviousPage}>前のページに戻る</button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </Layout>
    </React.Fragment>
  )
}

export default TrialLensConfirm;
